class FeaturedCollectionsV2 extends HTMLElement {
    constructor() {
      super();
      this.swipers = [];
      this.sectionId = this.getAttribute('data-section-id');
      this.radios = this.querySelectorAll("input[type='radio']");
  
      this.handleTabChange = this.handleTabChange.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      this.radios.forEach(radio => {
        radio.addEventListener('change', this.handleTabChange);
      });

      window.addEventListener('resize', this.handleResize);

      requestAnimationFrame(() => {
        this.initVisibleSwipers();
      });
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);
      this.swiperDestroy();
    }

    handleResize() {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this.equalizeAllVisible(), 150);
    }
  
    handleTabChange() {
      const allSwiperContainers = this.querySelectorAll('.featured-collections-v2__swiper');
      const selectedRadio = this.querySelector("input[type='radio']:checked");
      if (!selectedRadio) return;
  
      const targetSwiperId = selectedRadio.dataset.targetSwiper;
  
      allSwiperContainers.forEach(container => {
        if (container.id === targetSwiperId) {
          container.classList.remove('hidden');
        } else {
          container.classList.add('hidden');
        }
      });
  
      requestAnimationFrame(() => {
        this.refreshSwiperForVisiblePanel();
        const visible = this.querySelector('.featured-collections-v2__swiper:not(.hidden)');
        if (visible) this.equalizeSlideHeights(visible);
      });
    }

    equalizeSlideHeights(swiperEl) {
      if (!swiperEl) return;
      const slides = swiperEl.querySelectorAll('.swiper-wrapper > .swiper-slide');
      if (!slides.length) return;

      slides.forEach(s => { s.style.minHeight = ''; s.style.height = 'auto'; });

      let max = 0;
      slides.forEach(s => {
        const h = s.getBoundingClientRect().height;
        if (h > max) max = h;
      });

      if (max > 0) {
        slides.forEach(s => { s.style.minHeight = max + 'px'; });
        if (swiperEl.swiper && typeof swiperEl.swiper.update === 'function') {
          swiperEl.swiper.update();
        }
      }
    }

    equalizeAllVisible() {
      this.querySelectorAll('.featured-collections-v2__swiper').forEach(el => {
        this.equalizeSlideHeights(el);
      });
    }
  
    initVisibleSwipers() {
      this.querySelectorAll('.featured-collections-v2__swiper:not(.hidden)').forEach(el => {
        this.createSwiperIfNeeded(el);
      });
    }
  
    refreshSwiperForVisiblePanel() {
      const visible = this.querySelector('.featured-collections-v2__swiper:not(.hidden)');
      if (!visible) return;
  
      if (visible.swiper) {
        const swiper = visible.swiper;
        swiper.update();
        swiper.updateSlides();
        swiper.updateSlidesClasses();
        swiper.updateProgress();
        if (swiper.params.loop && typeof swiper.loopFix === 'function') {
          swiper.loopFix();
        }
      } else {
        this.createSwiperIfNeeded(visible);
      }
    }
  
    createSwiperIfNeeded(swiperEl) {
      if (!swiperEl || swiperEl.swiper) return;

      let slideCount = swiperEl.querySelectorAll('.swiper-wrapper > .swiper-slide').length;

      const desktopGap = parseInt(swiperEl.dataset.productGridGap, 10) || 64;
      const mobileGapEnabled = swiperEl.dataset.productGridGapMobileEnable === 'true';
      const mobileGapParsed = parseInt(swiperEl.dataset.productGridGapMobile, 10);
      const mobileGap = mobileGapEnabled
        ? (Number.isNaN(mobileGapParsed) ? 16 : mobileGapParsed)
        : 16;

      const itemsPerRow = parseInt(swiperEl.dataset.itemsPerRow, 10) || parseInt(swiperEl.dataset.productsOnDesktop, 10) || 4;
      const slideHalfDesktop = this.hasAttribute('data-slide-half-desktop');
      const isNarrow = swiperEl.dataset.containerWidth === 'narrow';
      const showPeek = swiperEl.dataset.showSlidePeek !== 'false';
      const disableLoop = this.dataset.disableCarouselLoop === 'true';
      const stableCarouselArrows = swiperEl.dataset.stableCarouselArrows === 'true';

      // Narrow mode or no-peek mode: left-align the slider.
      // Full mode with peek: keep the original centered full-bleed behavior.
      const centered = !isNarrow && showPeek;

      let desktopPerView;
      if (!showPeek) {
        // Stick exactly to what's selected - no peek of next slide.
        desktopPerView = itemsPerRow;
      } else if (slideHalfDesktop) {
        desktopPerView = Math.max(1.5, itemsPerRow / 2);
      } else {
        // Peek mode: show 0.7 of the next slide beyond what's selected.
        desktopPerView = itemsPerRow + 0.6;
      }

      // Swiper loop mode requires slideCount >= 2 * slidesPerView. With small product lists
      // (e.g. 7 slides) and desktopPerView=4.6, this rule is violated and surfaces as:
      //   - prev-button stutter / whitespace on desktop arrow navigation
      //   - scroll-past-end whitespace on mobile drag
      // Workaround: clone real slides into the wrapper until the threshold is met. Swiper then
      // creates its own loop clones on top of the doubled set, restoring continuous loop math.
      if (!disableLoop && slideCount > 0) {
        const minSlidesForLoop = Math.max(Math.ceil(desktopPerView * 2) + 1, 6);
        if (slideCount < minSlidesForLoop) {
          const wrapper = swiperEl.querySelector('.swiper-wrapper');
          if (wrapper) {
            const originals = Array.from(wrapper.querySelectorAll(':scope > .swiper-slide'));
            const cyclesNeeded = Math.ceil(minSlidesForLoop / slideCount) - 1;
            for (let cycle = 0; cycle < cyclesNeeded; cycle++) {
              originals.forEach((slide) => {
                wrapper.appendChild(slide.cloneNode(true));
              });
            }
            slideCount = wrapper.querySelectorAll(':scope > .swiper-slide').length;
          }
        }
      }

      const mobilePerView = (perView) => (showPeek ? perView : Math.floor(perView) || 1);
      // On mobile/tablet (<990px) always left-align so the peek slide sits flush with the right edge.
      // Desktop keeps the configured centering behavior.
      const mobileCentered = false;

      const smallerProductCardsMobile = swiperEl.dataset.smallerProductCardsMobile === 'true';

      // Optional per-section override: forces a fixed mobile slidesPerView regardless of peek toggle.
      // Only sections that opt in via [data-mobile-slides-per-view] (e.g. slider-products) are affected.
      const mobileOverride = parseFloat(swiperEl.dataset.mobileSlidesPerView);
      const hasMobileOverride = !Number.isNaN(mobileOverride) && mobileOverride > 0;
      const overrideOr = (defaultValue) => (hasMobileOverride ? mobileOverride : defaultValue);

      // Landing-page mode: desktop is no-peek (4 full cards), but tablet/mobile still peek like the live site.
      // Opt-in per section via the "Landing page section" customizer setting.
      const isLandingPage = this.classList.contains('featured-collections-v2--landing-page');

      const mobileBreakpointsBase = (a, b, c) => {
        if (smallerProductCardsMobile) {
          const auto = { slidesPerView: 'auto', centeredSlides: mobileCentered };
          return {
            0: auto,
            460: auto,
            750: auto,
          };
        }
        return {
          0: { slidesPerView: overrideOr(mobilePerView(a)), centeredSlides: mobileCentered },
          460: { slidesPerView: overrideOr(mobilePerView(b)), centeredSlides: mobileCentered },
          750: { slidesPerView: overrideOr(mobilePerView(c)), centeredSlides: mobileCentered },
        };
      };

      let breakpoints;
      if (smallerProductCardsMobile) {
        // CSS controls slide widths at <990px via integer-pixel media queries.
        // slidesPerView: 'auto' tells Swiper to read those CSS widths instead of dividing
        // the container by a numeric slidesPerView — using a number here makes Swiper
        // inline-style the slide widths (overriding our CSS) and the resulting transform
        // math drifts away from the rendered widths, producing scroll-past-end whitespace.
        breakpoints = {
          0: { slidesPerView: 'auto', centeredSlides: false },
          460: { slidesPerView: 'auto', centeredSlides: false },
          750: { slidesPerView: 'auto', centeredSlides: false },
          990: { slidesPerView: desktopPerView, centeredSlides: centered, spaceBetween: desktopGap },
        };
      } else if (isLandingPage) {
        breakpoints = {
          0: { slidesPerView: 1.5, centeredSlides: false },
          750: { slidesPerView: 2.4, centeredSlides: false },
          990: { slidesPerView: 3.2, centeredSlides: false },
          1280: { slidesPerView: desktopPerView, centeredSlides: centered, spaceBetween: desktopGap },
        };
      } else if (slideHalfDesktop) {
        breakpoints = {
          0: { slidesPerView: 1.3, centeredSlides: true },
          460: { slidesPerView: 1.3, centeredSlides: true },
          750: { slidesPerView: 2.2, centeredSlides: false },
          990: { slidesPerView: desktopPerView, centeredSlides: centered, spaceBetween: desktopGap },
        };
      } else {
        // 1.345 gives the Figma mobile card: 289px wide with a 12px gap at 393px.
        breakpoints = {
          0: { slidesPerView: 1.345, centeredSlides: true },
          460: { slidesPerView: 1.345, centeredSlides: true },
          750: { slidesPerView: 2.2, centeredSlides: false },
          990: { slidesPerView: desktopPerView, centeredSlides: centered, spaceBetween: desktopGap },
        };
      }

      // loop + freeMode causes empty space while inertia coasts (Swiper #7553). Snap mode keeps loop continuous.
      // Extra loop clones help very wide viewports with fractional slidesPerView.
      const loopAdditionalSlides =
        !disableLoop && slideCount > 0
          ? Math.min(Math.max(Math.ceil(itemsPerRow * 2), 4), slideCount)
          : 0;

      const swiperInstance = new Swiper(swiperEl, {
        slidesPerView: smallerProductCardsMobile ? 'auto' : slideHalfDesktop ? 1.5 : 1.8,
        centeredSlides: smallerProductCardsMobile ? mobileCentered : centered,
        spaceBetween: mobileGap,
        autoHeight: false,
        // observer:true reinitializes Swiper on any DOM mutation; with our tab-switching flow
        // (hidden class toggle) it races with loopFix and re-introduces whitespace. We trigger
        // updates explicitly via refreshSwiperForVisiblePanel() instead.
        observer: !smallerProductCardsMobile,
        observeParents: !smallerProductCardsMobile,
        allowTouchMove: true,
        grabCursor: true,
        mousewheel: false,
        navigation: {
          nextEl: swiperEl.querySelector('.featured-collections-v2__swiper-next'),
          prevEl: swiperEl.querySelector('.featured-collections-v2__swiper-prev'),
        },
        breakpoints,
        loop: !disableLoop,
        ...(disableLoop ? {} : { loopAdditionalSlides, loopedSlides: slideCount }),
        // roundLengths breaks slidesPerView:'auto' + loop on mobile: per-slide rounding
        // accumulates drift across real + cloned slides, producing scroll-past-end whitespace.
        roundLengths: !smallerProductCardsMobile,
        watchOverflow: true,
        on: {
          init: (sw) => {
            requestAnimationFrame(() => this.equalizeSlideHeights(swiperEl));
          },
          breakpoint: (sw) => {
            requestAnimationFrame(() => {
              if (typeof sw.loopFix === 'function') sw.loopFix();
              this.equalizeSlideHeights(swiperEl);
            });
          },
          imagesReady: () => {
            this.equalizeSlideHeights(swiperEl);
          },
        },
      });

      const imgs = swiperEl.querySelectorAll('img');
      imgs.forEach(img => {
        if (!img.complete) {
          img.addEventListener('load', () => this.equalizeSlideHeights(swiperEl), { once: true });
          img.addEventListener('error', () => this.equalizeSlideHeights(swiperEl), { once: true });
        }
      });

      this.swipers.push(swiperInstance);
    }
  
    swiperDestroy() {
      if (this.swipers.length > 0) {
        this.swipers.forEach(swiper => swiper.destroy(true, true));
        this.swipers = [];
      }
    }
  }
  
  if (!customElements.get('featured-collections-v2')) {
    customElements.define('featured-collections-v2', FeaturedCollectionsV2);
  }
  